import React, { useEffect, useState } from "react";
import WeaponSelector from "../WeaponSelector/WeaponSelector";
import RangeSelector from "../RangeSelector/RangeSelector";

function CharacterWeapons({
  character,
  handleDiceRoll,
  editMode,
  handleSaveCharacter,
}) {
  const [weapons, setWeapons] = useState([...character.weapons]);
  const [showWeaponSelector, setShowWeaponSelector] = useState(false);
  const [showRangeSelector, setShowRangeSelector] = useState(false);
  const [selectedWeapon, setSelectedWeapon] = useState(null);

  useEffect(() => {
    if (!editMode) {
      setWeapons([...character.weapons]);
    }
  }, [editMode, character]);

  const handleUpdateWeapons = (index, field, value) => {
    const updatedWeapons = [...weapons];
    updatedWeapons[index][field] = value;

    // Se cambia l'associatedSkill, aggiorna anche il value
    if (field === "associatedSkill") {
      const skill = character.skills.find((s) => s.name === value);
      updatedWeapons[index].value = skill ? skill.value : "0";
    }

    setWeapons(updatedWeapons);
  };

  const handleAddWeapon = () => {
    setShowWeaponSelector(true);
  };

  const handleSelectWeapon = (weapon, baseSkillValue) => {
    const updatedWeapons = [...weapons];
    updatedWeapons.push(weapon);
    setWeapons(updatedWeapons);

    // Verifica se la skill associata esiste già nel personaggio
    const skillExists = character.skills.find(
      (skill) => skill.name === weapon.associatedSkill,
    );

    // Se la skill non esiste, aggiungila con il baseSkillValue del template
    if (!skillExists && weapon.associatedSkill) {
      const updatedSkills = [...character.skills];
      const skillValue = baseSkillValue?.toString() || "0";
      updatedSkills.push({
        name: weapon.associatedSkill,
        value: skillValue,
        baseValue: skillValue,
        category: "Combattimento", // Categoria di default per armi
        isSpecializable: true,
        sourceSkill: weapon.associatedSkill,
      });

      // Salva immediatamente il personaggio con le nuove armi e skills
      handleSaveCharacter({
        ...character,
        weapons: updatedWeapons,
        skills: updatedSkills,
      });
    } else {
      // Salva solo le armi aggiornate
      handleSaveCharacter({
        ...character,
        weapons: updatedWeapons,
      });
    }

    setShowWeaponSelector(false);
  };

  const handleDeleteWeapon = (index) => {
    if (window.confirm("Sei sicuro di voler cancellare questa arma?")) {
      const updatedWeapons = [...weapons];
      updatedWeapons.splice(index, 1);
      setWeapons(updatedWeapons);
    }
  };

  const handleWeaponClick = (weapon) => {
    // Se l'arma ha range multipli, mostra il popup per selezionare la distanza
    if (weapon.range && weapon.range.includes("/")) {
      setSelectedWeapon(weapon);
      setShowRangeSelector(true);
    } else {
      // Altrimenti tira i dadi direttamente
      handleDiceRoll(weapon);
    }
  };

  const handleRangeSelect = (rangeData) => {
    // Chiudi il popup e tira i dadi con il range selezionato
    setShowRangeSelector(false);
    handleDiceRoll(selectedWeapon, rangeData.range, rangeData.damage);
    setSelectedWeapon(null);
  };

  const weaponSkills = character.skills.filter(
    (skill) =>
      skill.name.indexOf("Armi") === 0 ||
      skill.name.indexOf("Combattere") === 0 ||
      skill.name.indexOf("Lanciare") === 0 ||
      skill.name.indexOf("Demolizioni") === 0 ||
      skill.name.indexOf("Artiglieria") === 0 ||
      skill.name.indexOf("Rip. elettriche") === 0,
  );

  const BD = character.points.find((point) => point.name === "Bonus al Danno");
  const FOR = character.characteristics.find((char) => char.name === "FOR");

  const evaluateDamage = (damage) => {
    return damage.replace(/BD/gi, `BD (${BD.value})`);
  };

  const evaluateRange = (range) => {
    // Se contiene "FOR/5", calcola il valore
    if (range && /FOR\/5/i.test(range)) {
      const forValue = FOR ? parseInt(FOR.value) : 0;
      const calculatedRange = Math.floor(forValue / 5);
      return `FOR/5: ${calculatedRange}m`;
    }
    return range;
  };

  // Ottiene il valore attuale della skill associata all'arma
  const getWeaponSkillValue = (weapon) => {
    if (!weapon.associatedSkill) return "-";

    const skill = character.skills.find(
      (s) => s.name === weapon.associatedSkill,
    );
    return skill ? skill.value : weapon.value || "-";
  };

  return (
    <>
      {showWeaponSelector && (
        <WeaponSelector
          character={character}
          onSelect={handleSelectWeapon}
          onClose={() => setShowWeaponSelector(false)}
        />
      )}
      {showRangeSelector && selectedWeapon && (
        <RangeSelector
          weapon={selectedWeapon}
          onSelect={handleRangeSelect}
          onClose={() => {
            setShowRangeSelector(false);
            setSelectedWeapon(null);
          }}
        />
      )}
      <div className="character-sheetpage_weapons">
        <div className="character-sheetpage_weapons_row">
          <div className="row_weapon_name">Armi</div>
          <div className="row_weapon_value">Abilità/Valore</div>
          <div className="row_weapon_damage">Danno</div>
          <div className="row_weapon_range">Gittata (m)</div>
          <div className="row_weapon_attacks">Attacchi</div>
          <div className="row_weapon_ammo">Munizioni</div>
          <div className="row_weapon_malfunction">Malf.</div>
          {editMode && <div className="row_weapon_delete"></div>}
        </div>
        {editMode
          ? weapons.map((weapon, index) => (
              <div
                key={`weapons_${index}`}
                className="character-sheetpage_weapons_row"
              >
                <div className="row_weapon_name">
                  <input
                    type="text"
                    value={weapon.name}
                    onChange={(e) =>
                      handleUpdateWeapons(index, "name", e.target.value)
                    }
                  />
                </div>
                <div className="row_weapon_value">
                  <select
                    value={weapon.associatedSkill || ""}
                    onChange={(e) =>
                      handleUpdateWeapons(
                        index,
                        "associatedSkill",
                        e.target.value,
                      )
                    }
                  >
                    <option value="">- Seleziona -</option>
                    {weaponSkills.map((skill, skillIndex) => (
                      <option key={skillIndex} value={skill.name}>
                        {skill.name} ({skill.value})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="row_weapon_damage">
                  <input
                    type="text"
                    value={weapon.damage}
                    onChange={(e) =>
                      handleUpdateWeapons(index, "damage", e.target.value)
                    }
                  />
                </div>
                <div className="row_weapon_range">
                  <input
                    type="text"
                    value={weapon.range}
                    onChange={(e) =>
                      handleUpdateWeapons(index, "range", e.target.value)
                    }
                  />
                </div>
                <div className="row_weapon_attacks">
                  <input
                    type="text"
                    value={weapon.attacks}
                    onChange={(e) =>
                      handleUpdateWeapons(index, "attacks", e.target.value)
                    }
                  />
                </div>
                <div className="row_weapon_ammo">
                  <input
                    type="text"
                    value={weapon.ammo}
                    onChange={(e) =>
                      handleUpdateWeapons(index, "ammo", e.target.value)
                    }
                  />
                </div>
                <div className="row_weapon_malfunction">
                  <input
                    type="text"
                    value={weapon.malfunction}
                    onChange={(e) =>
                      handleUpdateWeapons(index, "malfunction", e.target.value)
                    }
                  />
                </div>
                <div className="row_weapon_delete">
                  <button
                    onClick={() => handleDeleteWeapon(index)}
                    title="Elimina arma"
                    className="weapon-delete-btn"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))
          : weapons.map((weapon, index) => (
              <div
                key={`weapons_${index}`}
                className="character-sheetpage_weapons_row weapon-hover"
                onClick={() => handleWeaponClick(weapon)}
                title="Clicca per tirare i dadi"
              >
                <div className="row_weapon_name">{weapon.name}</div>
                <div className="row_weapon_value">
                  {getWeaponSkillValue(weapon)}
                </div>
                <div className="row_weapon_damage">
                  {evaluateDamage(weapon.damage)}
                </div>
                <div className="row_weapon_range">
                  {evaluateRange(weapon.range)}
                </div>
                <div className="row_weapon_attacks">{weapon.attacks}</div>
                <div className="row_weapon_ammo">{weapon.ammo}</div>
                <div className="row_weapon_malfunction">
                  {weapon.malfunction}
                </div>
              </div>
            ))}
      </div>
      {editMode && (
        <div className="character-sheetpage_weapons_submit">
          <button
            onClick={() => handleSaveCharacter({ ...character, weapons })}
          >
            Salva
          </button>
          <button onClick={() => handleAddWeapon()}>Aggiungi Arma</button>
        </div>
      )}
    </>
  );
}

export default CharacterWeapons;
